import { faShieldHalved } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { HighlightedText, HighlightedTextProps } from 'components/text/HighlightedText';
import { isKcfUser } from 'pages/Desk/issues/shared';
import { IssueUserInfo } from 'pages/Desk/issues/types';
import { memo } from 'react';
import { UserAvatar } from 'shared/user_avatar/UserAvatar';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';

const Container = styled.div`
    display: grid;
    grid-template-columns: auto 1fr;
    grid-template-areas: 'avatar user';
    column-gap: 0.8rem;
    align-items: center;
    padding: 1rem;
`;

const Avatar = styled(UserAvatar)`
    grid-area: avatar;
`;

const User = styled.div`
    grid-area: user;
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    word-break: break-all;
    gap: 0.25rem;
`;

const Shield = styled(FontAwesomeIcon)`
    margin-right: 0.25rem;
`;

const Name = styled.div`
    display: flex;
    align-items: center;
    gap: 0.3rem;
    color: ${tokens.text.primary};
    font-size: 1.4rem;
`;

const Email = styled.div`
    color: ${tokens.text.secondary};
    font-size: 1.2rem;
`;

const highlightRenderer: HighlightedTextProps['render'] = ({ text, isMatch }) =>
    isMatch ? <strong>{text}</strong> : text;

export type UserDetailsProps = Readonly<{
    user: IssueUserInfo;
    searchTerms?: string[];
    className?: string;
}>;

function UserDetails({ user, searchTerms = [], className }: UserDetailsProps) {
    const { id, email, firstName, lastName } = user;
    const showShield = isKcfUser(user.role);

    return (
        <Container key={id} aria-label={`${firstName} ${lastName}`} className={className}>
            <Avatar
                firstName={firstName}
                lastName={lastName}
                size="3.2rem"
                fontSize="1.2rem"
            />
            <User>
                <Name>
                    {showShield && <Shield icon={faShieldHalved} />}
                    <>
                        <HighlightedText
                            text={firstName}
                            terms={searchTerms}
                            render={highlightRenderer}
                            mode="contains"
                        />{' '}
                        <HighlightedText
                            text={lastName}
                            terms={searchTerms}
                            render={highlightRenderer}
                            mode="contains"
                        />
                    </>
                </Name>
                <Email>
                    <HighlightedText
                        text={email}
                        terms={searchTerms}
                        render={highlightRenderer}
                        mode="contains"
                    />
                </Email>
            </User>
        </Container>
    );
}

const memoUserDetails = memo(UserDetails);
export { memoUserDetails as UserDetails };
